import { useTranslation } from 'react-i18next'
import { useEffect, useContext, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Breadcrumbs, ListItemButton, Typography } from '@mui/material'

import Dialog from '../components/common/dialog'
import { requestAccessToken } from '../api/auth.api'
import NextIcon from '../assets/icons/next-icon.svg'
import { Customer, BankAccount, FormError } from '../types'
import { customersBankAccountType } from '../lib/common.lib'
import { handleCheckIfBankAccountNumberIsValid } from '../utils'
import ActionsBar from '../components/bank-accounts-management/actions-bar'
import BankAccountForm from '../components/bank-accounts-management/bank-account-form'
import { handleUpdateDataByOne } from '../utils/db/common-methods'
import BankAccountListItem from '../components/bank-accounts-management/bank-account-list-item'
import { createBankAccount, deleteBankAccount, setBankAccountAsMain } from '../api/customers.api'
import { AppStateContext, AuthContext, UserContext, NotificationsContext } from '../contexts'
import { getCustomerDetailsData } from '../api/app-data.api'
import { getCustomersDataWithSameBankAccount } from '../api/app-queries.api'
import { useSocket } from '../hooks/use-socket'

const BanksAccountsManagement = () => {
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'

  const { t } = useTranslation(['bank-accounts-management', 'payments-to-review-management', 'common'])

  const navigate = useNavigate()
  const { customerId } = useParams()
  const { userSession } = useContext(UserContext)
  const { isAuth, removeAuth } = useContext(AuthContext)
  const { appDataSocket, isAppDataSocketInitialized } = useSocket()
  const { banksData, updateLoadingStatus } = useContext(AppStateContext)
  const { updateNewNotificationStatus } = useContext(NotificationsContext)

  const [errors, setErrors] = useState<FormError>({})
  const [customerData, setCustomerData] = useState<Partial<Customer>>({})
  const [bankAccountData, setBankAccountData] = useState<Partial<BankAccount>>({})
  const [openNewBankAccountModal, setOpenNewBankAccountModal] = useState<boolean>(false)
  const [openDeleteBankAccountModal, setOpenDeleteBankAccountModal] = useState<boolean>(false)
  const [bankAccountSelectedData, setBankAccountSelectedData] = useState<Partial<BankAccount>>({})
  const [banksAccountListData, setBanksAccountListData] = useState<Array<Partial<BankAccount>>>([])
  const [dialogNewBankAccountIsLoading, setDialogNewBankAccountIsLoading] = useState<boolean>(false)
  const [dialogDeleteBankAccountIsLoading, setDialogDeleteBankAccountIsLoading] = useState<boolean>(false)

  useEffect(() => {
    if (!appDataSocket) return

    if (!userSession) return

    appDataSocket.on(`customerUpdated:${customerId}`, async (dataFromSocket: any) => {
      if (dataFromSocket.deleted === false) {
        handleGetCustomerDetailsData()
      }

      if (dataFromSocket.deleted === true) {
        navigate('/beneficiaries')
      }
    })

    appDataSocket.on(`customerDeleted:${customerId}`, async () => {
      navigate('/beneficiaries')
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession, appDataSocket, isAppDataSocketInitialized])

  useEffect(() => {
    handleGetCustomerDetailsData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleGetCustomerDetailsData = async () => {
    let localCustomerDetailsDataToSet: any = {}
    const localCustomerDetailsData = globalThis.sessionStorage.getItem(`customerDetailsData:${customerId}`)

    if (localCustomerDetailsData) {
      localCustomerDetailsDataToSet = JSON.parse(localCustomerDetailsData)
      handleSetInitialData(localCustomerDetailsDataToSet)
    }

    try {
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const { data } = await getCustomerDetailsData(String(customerId), accessToken)

      handleSetInitialData(data)
      globalThis.sessionStorage.setItem(`customerDetailsData:${customerId}`, JSON.stringify(data))
    } catch (error) {
      console.log('Error handleGetCustomerDetailsData > ', error)
    }
  }

  const handleSetInitialData = async (dataToSet: any) => {
    const customerCountry = dataToSet.customerData.country || 'COL'

    if (customerCountry.toUpperCase() !== selectedCountry) {
      navigate('/beneficiaries')
      return
    }

    if (customerCountry.toUpperCase() === 'COL') {
      const bankAccountsDataToSet = Array.isArray(dataToSet.customerData.bankAccountsData)
        ? dataToSet.customerData.bankAccountsData?.map((bankAccountData: BankAccount) => {
            if (bankAccountData.bankAccountType === '27') {
              bankAccountData.bankAccountType = '1'
            } else if (bankAccountData.bankAccountType === '37') {
              bankAccountData.bankAccountType = '2'
            }

            return bankAccountData
          })
        : [] // --> Esto lo hacemos por el formato del cliente

      setBanksAccountListData(bankAccountsDataToSet || [{}])
    } else {
      setBanksAccountListData(dataToSet.customerData.bankAccountsData || [{}])
    }

    console.log('dataToSet', dataToSet)
    setCustomerData(dataToSet.customerData)
  }

  const handleValidateNewBankAccountForm = async () => {
    setErrors({})
    let formDataIsValid = true
    const formErrors: FormError = {}
    const bankAccountAlreadyAdded = banksAccountListData.find((bankAccountItemData) => bankAccountItemData.bankNumber === bankAccountData.bankNumber)

    if (!bankAccountData.bankAccountType) {
      formDataIsValid = false
      formErrors.bankAccountType = t('newBankAccountModal.bankAccountForm.errors.emptyBankAccountType')
    }

    if (selectedCountry === 'ARG' || selectedCountry === 'CHI' || selectedCountry === 'PER' || selectedCountry === 'BRA' || (selectedCountry === 'COL' && bankAccountData.bankAccountType !== 'breb')) {
      if (!bankAccountData.bankName) {
        formDataIsValid = false
        formErrors.bankName = t('newBankAccountModal.bankAccountForm.errors.emptyBankName')
      }
    }

    if (!bankAccountData.bankNumber) {
      formDataIsValid = false
      formErrors.bankAccountNumber = t('newBankAccountModal.bankAccountForm.errors.emptyBankAccountNumber')
    }

    if (bankAccountData.bankNumber && bankAccountAlreadyAdded) {
      formDataIsValid = false
      formErrors.bankAccountNumber = t('newBankAccountModal.bankAccountForm.errors.duplicateBankAccountNumber')
    }

    if (selectedCountry === 'COL' && bankAccountData.bankAccountType !== 'breb') {
      if (bankAccountData.bankNumber && Number.isNaN(Number(bankAccountData.bankNumber.trim()))) {
        formDataIsValid = false
        formErrors.bankAccountNumber = t('bankAccountNoveltyForm.errorMessages.hasToBeOnlyDigits', { ns: 'payments-to-review-management' })
      }

      if (bankAccountData.bankNumber && !Number.isNaN(Number(bankAccountData.bankNumber.trim()))) {
        const bankAccountNumberIsInvalid = handleCheckIfBankAccountNumberIsValid(bankAccountData as { bankAchCode: string }, bankAccountData.bankNumber.trim(), t)

        if (bankAccountNumberIsInvalid) {
          formDataIsValid = false
          formErrors.bankAccountNumber = bankAccountNumberIsInvalid
        }
      }
    }

    if (selectedCountry === 'BRA') {
      if (!bankAccountData.pix) {
        formDataIsValid = false
        formErrors.pixKey = t('newBankAccountModal.bankAccountForm.errors.emptyPixKey')
      }
    }

    if (!formDataIsValid) {
      return setErrors(formErrors)
    }

    setDialogNewBankAccountIsLoading(true)
    const {
      data: { accessToken }
    } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
    const { data } = await getCustomersDataWithSameBankAccount(String(userSession?.belongsTo), String(customerId), String(bankAccountData.bankNumber?.trim()), accessToken)

    if (Number.isNaN(Number(data)) || (!Number.isNaN(Number(data)) && data > 0)) {
      formDataIsValid = false
      formErrors.bankAccountNumber = t('bankAccountNoveltyForm.errorMessages.duplicateBankAccount', { ns: 'payments-to-review-management' })
    }

    if (!formDataIsValid) {
      setDialogNewBankAccountIsLoading(false)
      return setErrors(formErrors)
    }

    try {
      const bankAccountDataToCreate = {
        ...bankAccountData,
        bankAccountMain: Array.isArray(customerData.bankAccountsData) && customerData.bankAccountsData.length === 0 ? true : bankAccountData.bankAccountMain
      }
      await createBankAccount(bankAccountDataToCreate, accessToken, customerId)

      setBankAccountData({})
      setOpenNewBankAccountModal(false)
      setDialogNewBankAccountIsLoading(false)
      updateNewNotificationStatus?.(true, 'bank-account-created')
    } catch (error: any) {
      console.log('Error handleValidateNewCustomerForm', error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401 && error?.response?.data.message === 'Not authorized: Exists') {
        setErrors({ generalError: `${t('formGeneralErrors.unauthorized', { ns: 'common' })}: 406` })
      } else if (error?.response?.status === 401 && !(error?.response?.data.message === 'Not authorized: Exists')) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setDialogNewBankAccountIsLoading(false)
    } finally {
      setDialogNewBankAccountIsLoading(false)
    }
  }

  const handleConfirmBankAccountDeletion = async () => {
    setErrors({})

    try {
      setDialogDeleteBankAccountIsLoading(true)
      const accessTokenData = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const bankAccountDeleted = await deleteBankAccount(String(bankAccountSelectedData.bankNumber), accessTokenData.data.accessToken, customerId)

      await handleUpdateDataByOne('customers', String(bankAccountDeleted.data._id), bankAccountDeleted.data)

      setOpenDeleteBankAccountModal(false)
      setDialogDeleteBankAccountIsLoading(false)

      if (updateNewNotificationStatus) {
        updateNewNotificationStatus(true, 'bank-account-deleted')
      }
    } catch (error: any) {
      console.log('Error handleValidateNewCustomerForm', error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401 && error?.response?.data.message === 'Not authorized: Exists') {
        setErrors({ generalError: `${t('formGeneralErrors.unauthorized', { ns: 'common' })}: 406` })
      } else if (error?.response?.status === 401 && !(error?.response?.data.message === 'Not authorized: Exists')) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setDialogDeleteBankAccountIsLoading(false)
    } finally {
      setDialogDeleteBankAccountIsLoading(false)
    }
  }

  const handleSetBankAccountAsMain = async (bankAccountNumber: string) => {
    if (updateLoadingStatus) updateLoadingStatus(true)

    try {
      const accessTokenData = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const bankAccountSettedAsMain = await setBankAccountAsMain(bankAccountNumber, accessTokenData.data.accessToken, customerId)
      await handleUpdateDataByOne('customers', String(bankAccountSettedAsMain.data._id), bankAccountSettedAsMain.data)

      if (updateLoadingStatus) updateLoadingStatus(false)

      if (updateNewNotificationStatus) updateNewNotificationStatus(true, 'bank-account-setted-as-main')
    } catch (error: any) {
      console.log('Error handleValidateNewCustomerForm', error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401 && error?.response?.data.message === 'Not authorized: Exists') {
        setErrors({ generalError: `${t('formGeneralErrors.unauthorized', { ns: 'common' })}: 406` })
      } else if (error?.response?.status === 401 && !(error?.response?.data.message === 'Not authorized: Exists')) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      if (updateLoadingStatus) updateLoadingStatus(false)
    } finally {
      if (updateLoadingStatus) updateLoadingStatus(false)
    }
  }

  const handleCloseSession = () => {
    if (removeAuth) {
      removeAuth()
      navigate('/', { replace: true, state: { sessionExpired: true } })
    }
  }

  return (
    <>
      <div className="section-container">
        <Breadcrumbs
          separator={
            <img
              src={NextIcon}
              alt="next-icon"
            />
          }
          sx={{ width: '100%' }}
          aria-label="breadcrumb"
        >
          <ListItemButton
            sx={{
              '&.MuiButtonBase-root': {
                padding: '0px'
              },
              '&.MuiButtonBase-root:hover': {
                backgroundColor: 'rgba(0,0,0,0)'
              }
            }}
            onClick={() => navigate('/beneficiaries')}
          >
            <Typography
              key="3"
              className="breadcrumb-inactive"
            >
              {t('breadcrumbs.firstLevel')}
            </Typography>
          </ListItemButton>
          <ListItemButton
            sx={{
              '&.MuiButtonBase-root': {
                padding: '0px'
              },
              '&.MuiButtonBase-root:hover': {
                backgroundColor: 'rgba(0,0,0,0)'
              }
            }}
            onClick={() => navigate(`/beneficiary-details/${customerId}`)}
          >
            <Typography
              key="3"
              className="breadcrumb-inactive"
            >
              {t('breadcrumbs.secondLevel')}
            </Typography>
          </ListItemButton>
          <Typography
            key="3"
            className="breadcrumb-active"
          >
            {t('breadcrumbs.thirdLevel')}
          </Typography>
        </Breadcrumbs>
        <ActionsBar handleOpenNewBankAccountModal={() => setOpenNewBankAccountModal(true)} />
        <div className="bank-accounts-list-container">
          {banksAccountListData.map((bankAccountData: Partial<BankAccount>) => {
            return (
              <BankAccountListItem
                key={bankAccountData._id}
                bankAccountData={bankAccountData}
                handleSetBankAccountAsMain={handleSetBankAccountAsMain}
                showMoreActionsButton={banksAccountListData.length > 1}
                handleOpenDeleteBankAccountModal={(bankAccountToDelete: Partial<BankAccount>) => {
                  setOpenDeleteBankAccountModal(true)
                  setBankAccountSelectedData(bankAccountToDelete)
                }}
              />
            )
          })}
        </div>
      </div>
      {openNewBankAccountModal && (
        <Dialog
          errors={errors}
          actionButtonText={t('newBankAccountModal.actionButtonText')}
          dialogTitle={t('newBankAccountModal.title')}
          dialogIsLoading={dialogNewBankAccountIsLoading}
          handleDialogAction={handleValidateNewBankAccountForm}
          handleDialogOpenState={(openState: boolean) => {
            setOpenNewBankAccountModal(openState)

            if (openState === false) {
              setErrors({})
              setBankAccountData({})
            }
          }}
        >
          <BankAccountForm
            errors={errors}
            banksData={banksData ?? []}
            bankAccountData={bankAccountData}
            handleSetBankAccountData={(bankAccountDataToAdd: BankAccount) => {
              setBankAccountData({ ...bankAccountDataToAdd })
            }}
            mainBankAccountAsDefault={Array.isArray(customerData.bankAccountsData) && customerData.bankAccountsData.length === 0}
          />
        </Dialog>
      )}
      {openDeleteBankAccountModal && (
        <Dialog
          errors={errors}
          actionButtonWarningStyle
          actionButtonText={t('deleteBankAccountModal.actionButtonText')}
          dialogTitle={t('deleteBankAccountModal.title')}
          dialogIsLoading={dialogDeleteBankAccountIsLoading}
          handleDialogAction={handleConfirmBankAccountDeletion}
          handleDialogOpenState={(openState: boolean) => {
            setOpenDeleteBankAccountModal(openState)

            if (openState === false) {
              setErrors({})
              setBankAccountSelectedData({})
            }
          }}
        >
          <div
            className="dialog-content-text"
            style={{ display: 'inline-block', textAlign: 'center' }}
          >
            {t('deleteBankAccountModal.text.firstPart')}{' '}
            <strong style={{ color: '#107569' }}>{`${(customersBankAccountType as any)[bankAccountSelectedData?.bankAccountType ?? '2']} No. ${bankAccountSelectedData?.bankNumber} ${t(
              'deleteBankAccountModal.text.secondPart'
            )} ${bankAccountSelectedData?.bankName}`}</strong>{' '}
            {t('deleteBankAccountModal.text.thirdPart')} <strong style={{ color: '#107569' }}>{`${customerData?.registeredFullName}`}</strong>?<br />
            <br />
            {t('deleteBankAccountModal.text.fourthPart')}
          </div>
        </Dialog>
      )}
    </>
  )
}

export default BanksAccountsManagement
